import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { ArrowLeft } from "lucide-react";

const CONTENT = {
  it: {
    title: "Privacy & Cookie Policy",
    updated: "Ultimo aggiornamento: giugno 2026",
    back: "Torna alla home",
    sections: [
      {
        h: "1. Titolare del trattamento",
        p: "ReValue (\"noi\") gestisce questo sito web che fornisce stime del prezzo di rivendita di prodotti usati. Per qualsiasi richiesta relativa ai tuoi dati puoi contattarci tramite i canali indicati sul sito.",
      },
      {
        h: "2. Dati che raccogliamo",
        p: "• Dati account: se ti registri, raccogliamo nome, email e password (memorizzata in forma cifrata).\n• Dati di valutazione: categoria, marca, modello, prezzi, condizioni ed eventuali foto che carichi per ottenere una stima. Le valutazioni salvate sono associate al tuo account.\n• Dati di utilizzo: ricerche effettuate nella barra di ricerca rapida (in forma aggregata, per le tendenze).",
      },
      {
        h: "3. Finalità e base giuridica",
        p: "Trattiamo i dati per: fornire il servizio di stima (esecuzione del contratto), salvare la cronologia delle valutazioni (esecuzione del contratto), migliorare il servizio (legittimo interesse) e mostrare pubblicità (consenso).",
      },
      {
        h: "4. Cookie tecnici",
        p: "Usiamo un cookie tecnico sicuro (HttpOnly) per mantenere la sessione di accesso e il localStorage del browser per ricordare la lingua scelta e la tua preferenza sui cookie. Questi elementi sono necessari al funzionamento del sito e non richiedono consenso.",
      },
      {
        h: "5. Cookie pubblicitari (Google AdSense)",
        p: "Con il tuo consenso, questo sito mostra annunci tramite Google AdSense. Google e i suoi partner possono usare cookie e identificatori per personalizzare gli annunci e misurarne le prestazioni. Se rifiuti il consenso, non vengono effettuate richieste pubblicitarie.\n\nPer maggiori informazioni: Norme privacy di Google (policies.google.com/privacy) e Come Google utilizza i cookie (policies.google.com/technologies/cookies). Puoi gestire la personalizzazione degli annunci su adssettings.google.com.",
      },
      {
        h: "6. Intelligenza artificiale",
        p: "Le descrizioni e le foto che invii per la stima vengono elaborate da un modello di intelligenza artificiale al solo scopo di generare la valutazione. Non usare foto o descrizioni contenenti dati personali di terzi.",
      },
      {
        h: "7. Conservazione e condivisione",
        p: "Le valutazioni salvate restano nel tuo account finché non le elimini. Se generi un link pubblico di condivisione, chiunque abbia il link può vedere la valutazione (senza prezzo d'acquisto né dati personali). Non vendiamo i tuoi dati a terzi.",
      },
      {
        h: "8. I tuoi diritti (GDPR)",
        p: "Hai diritto di accedere, rettificare, cancellare i tuoi dati, limitarne od opporti al trattamento e alla portabilità (artt. 15-22 GDPR). Puoi eliminare le singole valutazioni dalla cronologia in autonomia. Hai inoltre diritto di proporre reclamo al Garante per la protezione dei dati personali.",
      },
      {
        h: "9. Revoca del consenso ai cookie",
        p: "Puoi revocare o modificare il consenso ai cookie pubblicitari in qualsiasi momento cancellando i dati di navigazione del sito dal tuo browser: alla visita successiva il banner verrà mostrato di nuovo.",
      },
    ],
  },
  en: {
    title: "Privacy & Cookie Policy",
    updated: "Last updated: June 2026",
    back: "Back to home",
    sections: [
      {
        h: "1. Data controller",
        p: "ReValue (\"we\") operates this website providing resale price estimates for second-hand products. For any request regarding your data you can contact us through the channels listed on the site.",
      },
      {
        h: "2. Data we collect",
        p: "• Account data: if you sign up, we collect your name, email and password (stored encrypted).\n• Valuation data: category, brand, model, prices, condition and any photos you upload to get an estimate. Saved valuations are linked to your account.\n• Usage data: quick-search queries (in aggregate form, for trends).",
      },
      {
        h: "3. Purposes and legal basis",
        p: "We process data to: provide the estimation service (contract performance), save your valuation history (contract performance), improve the service (legitimate interest) and display advertising (consent).",
      },
      {
        h: "4. Technical cookies",
        p: "We use a secure technical cookie (HttpOnly) to keep your login session, and the browser localStorage to remember your language choice and your cookie preference. These are necessary for the site to work and do not require consent.",
      },
      {
        h: "5. Advertising cookies (Google AdSense)",
        p: "With your consent, this site shows ads through Google AdSense. Google and its partners may use cookies and identifiers to personalise ads and measure their performance. If you reject consent, no ad requests are made.\n\nMore info: Google Privacy Policy (policies.google.com/privacy) and How Google uses cookies (policies.google.com/technologies/cookies). You can manage ad personalisation at adssettings.google.com.",
      },
      {
        h: "6. Artificial intelligence",
        p: "The descriptions and photos you submit for an estimate are processed by an AI model for the sole purpose of generating the valuation. Do not use photos or descriptions containing third parties' personal data.",
      },
      {
        h: "7. Retention and sharing",
        p: "Saved valuations remain in your account until you delete them. If you generate a public share link, anyone with the link can view the valuation (without purchase price or personal data). We do not sell your data to third parties.",
      },
      {
        h: "8. Your rights (GDPR)",
        p: "You have the right to access, rectify, erase your data, restrict or object to processing, and data portability (Art. 15-22 GDPR). You can delete individual valuations from your history yourself. You also have the right to lodge a complaint with your data protection authority.",
      },
      {
        h: "9. Withdrawing cookie consent",
        p: "You can withdraw or change your consent to advertising cookies at any time by clearing this site's browsing data in your browser: the banner will be shown again on your next visit.",
      },
    ],
  },
};

export default function PrivacyPolicy() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.it;

  return (
    <main data-testid="privacy-page" className="bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/" data-testid="privacy-back" className="inline-flex items-center gap-2 font-mono text-xs font-bold tracking-widest text-[#4A4A4A] hover:text-black">
          <ArrowLeft size={14} /> {c.back}
        </Link>
        <span className="block overline text-[#002FA7] mt-6">LEGAL / PRIVACY</span>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">{c.title}</h1>
        <p className="font-mono text-xs text-[#737373] uppercase tracking-widest mt-3">{c.updated}</p>

        <div className="mt-10 space-y-8">
          {c.sections.map((s) => (
            <section key={s.h} className="border-l-2 border-black pl-5">
              <h2 className="font-display font-black text-lg tracking-tight">{s.h}</h2>
              <p className="text-sm text-[#4A4A4A] leading-relaxed mt-2 whitespace-pre-line">{s.p}</p>
            </section>
          ))}
        </div>
      </div>
    </main>
  );
}
